import { AutoComplete, Input, Select } from 'antd'

import AtomTreeSelect from '@/components/Atoms/AtomTreeSelect/AtomTreeSelect'
import HoverElement from '@/components/Atoms/HoverElement/HoverElement'
import Image, { useProductImages } from '@/components/Atoms/Image/Image'
import ListInput from '@/components/Atoms/ListInput/ListInput'
import Loader from '@/components/Atoms/Loader/Loader'
import SlotsValue from '@/components/Atoms/SlotsValue/SlotsValue'
import { formatCharacteristicValue } from '@/components/Filters/Filters.utils'
import {
  getApprovalStatusLabel,
  isApprovedStatus,
} from '@/utils/catalog/approvalStatus'
import {
  ARTICLE_MAX_LENGTH,
  SUPPLIER_ARTICLES_MAX_COUNT,
} from '@/utils/nomenclature/articles'
import { getBarcodeError, sanitizeBarcode } from '@/utils/nomenclature/barcode'
import {
  formatSlotsValue,
  formatSlotValuesList,
  getSlotFieldLabel,
  isSlottedCharacteristicType,
} from '@/utils/search/characteristics/slottedCharacteristics'

import {
  getMainFieldDisplayValue,
  getMainFieldIdValue,
  getMainFieldListValues,
  MAIN_FIELDS,
} from '../../../../AttributeProcessing.types'
import { useAttributeProcessingContext } from '../../../../AttributeProcessingContext'
import cn from './ProcessingTab.module.scss'
import SupplierArticlesClearPicker from './SupplierArticlesClearPicker/SupplierArticlesClearPicker'
import SupplierArticlesValue from './SupplierArticlesValue/SupplierArticlesValue'

function ValueInput() {
  const {
    action,
    currentMainField,
    refFieldLoading,
    activeTab,
    selectedMainField,
    processValue,
    setProcessValue,
    processValues,
    setProcessValues,
    processValuesError,
    brands,
    brandsLoading,
    loadBrands,
    countries,
    countriesLoading,
    loadCountries,
    units,
    unitsLoading,
    loadUnits,
    globalCategoriesTree,
    antdCategoriesTreeLeafOnly,
    loadCategories,
    globalGroupsTree,
    antdParentsTreeLeafOnly,
    loadParents,
    selectedChrcCode,
    intersectedCharacteristics,
    dictionaryLoading,
    dictionaryValues,
    availableCharacteristics,
    processSlotValues,
    updateSlotValue,
    slottedGapIndex,
    processSlotsMismatch,
  } = useAttributeProcessingContext()

  if (action === 'CLEAR') return null
  if (currentMainField?.readonly) return null
  if (refFieldLoading) return <div className={cn.valueSkeleton} />

  if (activeTab === 'MAIN' && selectedMainField) {
    const field = MAIN_FIELDS.find((f) => f.key === selectedMainField)
    if (!field) return null

    if (field.key === 'DETAILS') {
      return (
        <Input.TextArea
          className={cn.valueTextarea}
          value={processValue}
          onChange={(e) => setProcessValue(e.target.value)}
          placeholder="Введите текст..."
          autoSize={{ minRows: 2, maxRows: 4 }}
        />
      )
    }

    if (field.key === 'BARCODE') {
      return (
        <>
          <input
            type="text"
            inputMode="numeric"
            className={cn.valueInput}
            value={processValue}
            onChange={(e) => setProcessValue(sanitizeBarcode(e.target.value))}
            placeholder="Введите штрихкод..."
          />
          {getBarcodeError(processValue) && (
            <span className={cn.valueError}>
              {getBarcodeError(processValue)}
            </span>
          )}
        </>
      )
    }

    switch (field.type) {
      case 'STRING_LIST':
        return (
          <ListInput
            value={processValues}
            onChange={setProcessValues}
            error={processValuesError ?? undefined}
            placeholder="Артикул поставщика"
            addLabel="Добавить артикул"
            maxCount={SUPPLIER_ARTICLES_MAX_COUNT}
            maxLength={ARTICLE_MAX_LENGTH}
          />
        )
      case 'NUMERIC':
        return (
          <input
            type="number"
            className={cn.valueInput}
            value={processValue}
            onChange={(e) => setProcessValue(e.target.value)}
            placeholder="Введите число..."
          />
        )
      case 'BOOLEAN':
        return (
          <select
            className={cn.valueSelect}
            value={processValue}
            onChange={(e) => setProcessValue(e.target.value)}
          >
            <option value="">— Выберите —</option>
            <option value="true">Да</option>
            <option value="false">Нет</option>
          </select>
        )
      case 'REFERENCE_BRAND':
        return (
          <Select
            className={cn.antSelect}
            showSearch
            placeholder="Выберите бренд..."
            value={processValue || undefined}
            onChange={(v) => setProcessValue(v)}
            onOpenChange={(open) => {
              if (open) loadBrands()
            }}
            loading={brandsLoading}
            notFoundContent={brandsLoading ? <Loader size={18} /> : undefined}
            filterOption={(input, opt) =>
              (opt?.label as string)
                ?.toLowerCase()
                .includes(input.toLowerCase())
            }
            options={brands.map((b) => ({ value: b.id, label: b.description }))}
          />
        )
      case 'REFERENCE_COUNTRY':
        return (
          <Select
            className={cn.antSelect}
            showSearch
            placeholder="Выберите страну..."
            value={processValue || undefined}
            onChange={(v) => setProcessValue(v)}
            onOpenChange={(open) => {
              if (open) loadCountries()
            }}
            loading={countriesLoading}
            notFoundContent={
              countriesLoading ? <Loader size={18} /> : undefined
            }
            filterOption={(input, opt) =>
              (opt?.label as string)
                ?.toLowerCase()
                .includes(input.toLowerCase())
            }
            options={countries.map((c) => ({
              value: c.id,
              label: c.description,
            }))}
          />
        )
      case 'REFERENCE_UNIT':
        return (
          <Select
            className={cn.antSelect}
            showSearch
            placeholder="Выберите единицу измерения..."
            value={processValue || undefined}
            onChange={(v) => setProcessValue(v)}
            onOpenChange={(open) => {
              if (open) loadUnits()
            }}
            loading={unitsLoading}
            notFoundContent={unitsLoading ? <Loader size={18} /> : undefined}
            filterOption={(input, opt) =>
              (opt?.label as string)
                ?.toLowerCase()
                .includes(input.toLowerCase())
            }
            options={units.map((u) => ({ value: u.id, label: u.description }))}
          />
        )
      case 'REFERENCE_CATEGORY':
        return (
          <HoverElement>
            <AtomTreeSelect
              tree={globalCategoriesTree}
              antdTree={antdCategoriesTreeLeafOnly}
              openByHover={false}
              value={processValue}
              className={cn.antSelect}
              placeholder="Выберите категорию..."
              onOpen={loadCategories}
              handleChange={(_, v) => setProcessValue(v ?? '')}
              handleClear={() => setProcessValue('')}
            />
          </HoverElement>
        )
      case 'REFERENCE_PARENT':
        return (
          <HoverElement>
            <AtomTreeSelect
              tree={globalGroupsTree}
              antdTree={antdParentsTreeLeafOnly}
              openByHover={false}
              value={processValue}
              className={cn.antSelect}
              placeholder="Выберите группу номенклатуры..."
              onOpen={loadParents}
              handleChange={(_, v) => setProcessValue(v ?? '')}
              handleClear={() => setProcessValue('')}
            />
          </HoverElement>
        )
      default:
        return (
          <input
            type="text"
            className={cn.valueInput}
            value={processValue}
            onChange={(e) => setProcessValue(e.target.value)}
            placeholder="Введите значение..."
          />
        )
    }
  }

  if (activeTab === 'ADDITIONAL' && selectedChrcCode !== null) {
    const chrc = intersectedCharacteristics.find(
      (c) => c.chrcCode === selectedChrcCode
    )
    if (!chrc) return null
    const characteristicUnit =
      availableCharacteristics
        .find((characteristic) => characteristic.code === selectedChrcCode)
        ?.unit?.trim() ?? ''
    const withUnit = (value: string) =>
      characteristicUnit ? `${value} ${characteristicUnit}` : value

    if (isSlottedCharacteristicType(chrc.valueType)) {
      // В processSlotValues лежат valueId (записка §4a) — для превью
      // разворачиваем их в текстовые диаметры по справочнику
      const summary = formatSlotValuesList(
        processSlotValues.map(
          (valueId) =>
            dictionaryValues.find((d) => d.id === valueId)?.value ?? ''
        )
      )

      return (
        <div className={cn.slottedFields}>
          {processSlotValues.map((value, index) => {
            // Записка §5 п.6: поле N доступно, только если заполнены все 1..N-1
            const isLocked = index > 0 && !processSlotValues[index - 1]?.trim()

            return (
              <div
                className={cn.slottedField}
                key={index}
                title={
                  isLocked
                    ? `Заполните «${getSlotFieldLabel(chrc.chrcName, index)}», чтобы разблокировать это поле`
                    : undefined
                }
              >
                <span className={cn.slottedFieldLabel}>
                  {getSlotFieldLabel(chrc.chrcName, index + 1)}
                </span>
                <Select
                  className={cn.antSelect}
                  showSearch
                  allowClear
                  disabled={isLocked}
                  // Панель узкая: длинный плейсхолдер обрезался бы многоточием
                  placeholder={
                    isLocked ? 'Заполните предыдущее' : 'Выберите диаметр...'
                  }
                  value={value || undefined}
                  loading={dictionaryLoading}
                  notFoundContent={
                    dictionaryLoading ? <Loader size={18} /> : undefined
                  }
                  filterOption={(input, opt) =>
                    (opt?.label as string)
                      ?.toLowerCase()
                      .includes(input.toLowerCase())
                  }
                  onChange={(next) => updateSlotValue(index, next ?? '')}
                  // Записка §4a: на бэк уходит valueId, поэтому value опции — UUID
                  options={dictionaryValues.map((d) => ({
                    value: d.id,
                    label: withUnit(formatCharacteristicValue(d.value)),
                  }))}
                />
              </div>
            )
          })}

          {processSlotsMismatch && (
            <div className={cn.slottedWarning}>
              У выбранных товаров разное количество присоединений. Товары, где
              присоединений меньше, сохранены не будут — остальные обновятся.
            </div>
          )}

          {slottedGapIndex > 0 && (
            <div className={cn.slottedError}>
              Не заполнено присоединение {slottedGapIndex}. Присоединения
              заполняются последовательно.
            </div>
          )}

          <div className={cn.slottedSummary}>
            Итоговое значение: <strong>{summary || '—'}</strong>
          </div>
        </div>
      )
    }

    switch (chrc.valueType) {
      case 'NUMERIC':
        return (
          <input
            type="number"
            className={cn.valueInput}
            value={processValue}
            onChange={(e) => setProcessValue(e.target.value)}
            placeholder="Введите число..."
          />
        )
      case 'BOOLEAN':
        return (
          <select
            className={cn.valueSelect}
            value={processValue}
            onChange={(e) => setProcessValue(e.target.value)}
          >
            <option value="">— Выберите —</option>
            <option value="true">Да</option>
            <option value="false">Нет</option>
          </select>
        )
      case 'REFERENCE':
        return (
          <Select
            className={cn.antSelect}
            showSearch
            placeholder="Выберите значение..."
            value={processValue || undefined}
            onChange={(v) => setProcessValue(v)}
            loading={dictionaryLoading}
            notFoundContent={
              dictionaryLoading ? <Loader size={18} /> : undefined
            }
            filterOption={(input, opt) =>
              (opt?.label as string)
                ?.toLowerCase()
                .includes(input.toLowerCase())
            }
            options={dictionaryValues.map((d) => ({
              value: d.id,
              label: withUnit(formatCharacteristicValue(d.value)),
            }))}
          />
        )
      default: {
        const suggestions =
          availableCharacteristics.find((c) => c.code === selectedChrcCode)
            ?.options ?? []
        if (suggestions.length > 0) {
          return (
            <AutoComplete
              className={cn.antSelect}
              options={suggestions.map((o) => ({ value: o }))}
              value={processValue}
              onChange={(v) => setProcessValue(v)}
              placeholder="Введите строку..."
              filterOption={(input, opt) =>
                (opt?.value as string)
                  ?.toLowerCase()
                  .includes(input.toLowerCase())
              }
            />
          )
        }
        return (
          <input
            type="text"
            className={cn.valueInput}
            value={processValue}
            onChange={(e) => setProcessValue(e.target.value)}
            placeholder="Введите строку..."
          />
        )
      }
    }
  }

  return null
}

function InfoTable() {
  const {
    isRightPanelDisabled,
    selectedDetails,
    intersectedCharacteristics,
    activeTab,
    selectedChrcCode,
    handleChrcSelect,
    selectedMainField,
    handleMainFieldSelect,
  } = useAttributeProcessingContext()

  if (isRightPanelDisabled) return null
  if (activeTab === 'ADDITIONAL') {
    return (
      <>
        <div className={cn.infoTable}>
          <div className={cn.infoTableHeader}>
            <span>Характеристика</span>
            <span>Значение</span>
          </div>
          <div className={cn.infoTableBody}>
            {intersectedCharacteristics.length === 0 ? (
              <div className={cn.infoTableEmpty}>
                {selectedDetails.length > 0
                  ? 'Нет характеристик'
                  : 'Загрузка...'}
              </div>
            ) : (
              intersectedCharacteristics.map((chrc) => {
                const isSlotted = isSlottedCharacteristicType(chrc.valueType)
                const perProductValues =
                  selectedDetails.length === 0
                    ? []
                    : selectedDetails.map((d) => {
                        const found = d.characteristics.find(
                          (c) => c.chrcCode === chrc.chrcCode
                        )
                        // Итоговая строка слотовой характеристики собирается из
                        // slots (записка §1), chrcValue может быть пустым у
                        // незаполненного товара
                        return isSlotted
                          ? formatSlotsValue(found?.slots)
                          : (found?.chrcValue ?? '')
                      })
                // У выбранных товаров значение различается — показываем это
                // явно, чтобы «Установить» не затёр разные значения незаметно.
                const isMixed =
                  perProductValues.length > 1 &&
                  !perProductValues.every((v) => v === perProductValues[0])
                const rawValue = isMixed ? '' : (perProductValues[0] ?? '')
                let displayValue = rawValue
                if (isSlotted) {
                  displayValue = rawValue
                } else if (chrc.valueType === 'BOOLEAN') {
                  if (rawValue === 'true') displayValue = 'Да'
                  else if (rawValue === 'false') displayValue = 'Нет'
                } else if (chrc.valueType === 'REFERENCE') {
                  // chrcValue у REFERENCE — это уже текст значения справочника
                  displayValue = rawValue
                } else if (rawValue) {
                  displayValue = formatCharacteristicValue(rawValue)
                }
                const isSelected = selectedChrcCode === chrc.chrcCode
                return (
                  <div
                    key={chrc.chrcCode}
                    className={[
                      cn.infoTableRow,
                      isSelected ? cn.infoTableRowSelected : '',
                    ]
                      .filter(Boolean)
                      .join(' ')}
                    onClick={() =>
                      handleChrcSelect(chrc.chrcCode, chrc.chrcName)
                    }
                  >
                    <span title={chrc.chrcName}>{chrc.chrcName}</span>
                    {isMixed ? (
                      <span
                        className={cn.infoTableMixed}
                        title="У выбранных товаров разные значения"
                      >
                        разные значения
                      </span>
                    ) : isSlotted && displayValue ? (
                      <span title={displayValue}>
                        <SlotsValue
                          slots={
                            selectedDetails[0]?.characteristics.find(
                              (c) => c.chrcCode === chrc.chrcCode
                            )?.slots
                          }
                          slotsCount={chrc.slotsCount}
                        />
                      </span>
                    ) : (
                      <span title={displayValue}>{displayValue}</span>
                    )}
                  </div>
                )
              })
            )}
          </div>
        </div>
      </>
    )
  }

  return (
    <div className={cn.infoTable}>
      <div className={cn.infoTableHeader}>
        <span>Реквизит</span>
        <span>Значение</span>
      </div>
      <div className={cn.infoTableBody}>
        {MAIN_FIELDS.map((field) => {
          const values = selectedDetails.map((d) =>
            getMainFieldDisplayValue(d.nomenclatureExtendedModel, field.key)
          )
          const value =
            values.length > 0 && values.every((v) => v === values[0])
              ? values[0]
              : ''
          const isSelected = selectedMainField === field.key
          return (
            <div
              key={field.key}
              className={[
                cn.infoTableRow,
                isSelected ? cn.infoTableRowSelected : '',
              ]
                .filter(Boolean)
                .join(' ')}
              onClick={() => handleMainFieldSelect(field.key)}
            >
              <span title={field.label}>{field.label}</span>
              {field.key === 'SUPPLIER_ARTICLES' && value ? (
                <span className={cn.infoTableChipsCell}>
                  <SupplierArticlesValue
                    articles={getMainFieldListValues(
                      selectedDetails[0].nomenclatureExtendedModel,
                      field.key
                    )}
                  />
                </span>
              ) : (
                <span title={value}>{value}</span>
              )}
            </div>
          )
        })}
      </div>
    </div>
  )
}

function ProcessingTab() {
  const {
    isRightPanelDisabled,
    activeTab,
    isPanelLoading,
    selectedMainField,
    handleMainFieldSelect,
    selectedChrcCode,
    intersectedCharacteristics,
    handleChrcSelect,
    detailsLoading,
    action,
    setAction,
    setProcessValue,
    setProcessValues,
    processValues,
    isListFieldSelected,
    supplierArticleOptions,
    currentMainField,
    isClearDisabled,
    isSetDisabled,
    isProcessDisabled,
    processing,
    handleProcess,
    selectedIds,
    selectedDetails,
    imageVersion,
    availableCharacteristics,
    dictionaryValues,
  } = useAttributeProcessingContext()

  const singleCode =
    selectedIds.size === 1 && selectedDetails.length === 1
      ? selectedDetails[0].code
      : null
  const { images: productImages, loading: productImagesLoading } =
    useProductImages(singleCode, imageVersion)
  const characteristicStatusByCode = new Map(
    availableCharacteristics.map((characteristic) => [
      characteristic.code,
      characteristic.status,
    ])
  )
  const processingCharacteristicOptions = intersectedCharacteristics.map(
    (c) => {
      const status = characteristicStatusByCode.get(c.chrcCode)
      const isApproved = isApprovedStatus(status)
      const statusLabel = getApprovalStatusLabel(status)

      return {
        value: c.chrcCode,
        label: (
          <span className={cn.chrcOption}>
            <span
              className={[
                cn.chrcOptionDot,
                isApproved ? cn.chrcOptionDotApproved : cn.chrcOptionDotBlocked,
              ].join(' ')}
            />
            <span className={cn.chrcOptionTextWrap}>
              <span className={cn.chrcOptionText}>{c.chrcName}</span>
              {statusLabel && (
                <span className={cn.chrcOptionStatus}>{statusLabel}</span>
              )}
            </span>
          </span>
        ),
        searchText: c.chrcName,
        disabled: !isApproved,
      }
    }
  )

  return (
    <div className={cn.processingTabRoot}>
      <div
        className={[
          cn.processingBlock,
          isRightPanelDisabled ? cn.blockDisabled : '',
        ]
          .filter(Boolean)
          .join(' ')}
      >
        <div className={cn.processingTitle}>Обработка</div>

        <div className={cn.processingRow}>
          <label className={cn.processingLabel}>
            {activeTab === 'MAIN' ? 'Реквизит' : 'Доп. реквизит'}
          </label>
          {activeTab === 'MAIN' ? (
            <Select
              className={cn.processingSelect}
              placeholder="Выберите реквизит..."
              value={selectedMainField ?? undefined}
              onChange={handleMainFieldSelect}
              disabled={isRightPanelDisabled}
              showSearch
              filterOption={(input, opt) =>
                (opt?.label as string)
                  ?.toLowerCase()
                  .includes(input.toLowerCase())
              }
              options={MAIN_FIELDS.map((f) => ({
                value: f.key,
                label: f.label,
              }))}
            />
          ) : (
            <Select
              className={cn.processingSelect}
              placeholder="Выберите характеристику..."
              value={selectedChrcCode ?? undefined}
              onChange={(code) => {
                const chrc = intersectedCharacteristics.find(
                  (c) => c.chrcCode === code
                )
                if (chrc) handleChrcSelect(chrc.chrcCode, chrc.chrcName)
              }}
              disabled={isRightPanelDisabled || detailsLoading}
              showSearch
              filterOption={(input, opt) =>
                String(opt?.searchText || '')
                  .toLowerCase()
                  .includes(input.toLowerCase())
              }
              options={processingCharacteristicOptions}
              notFoundContent={
                detailsLoading ? <Loader size={16} /> : 'Нет характеристик'
              }
            />
          )}
        </div>

        <div className={cn.processingRow}>
          <label className={cn.processingLabel}>Действие</label>
          <div className={cn.actionToggle}>
            <button
              className={[
                cn.actionBtn,
                action === 'SET' ? cn.actionBtnActive : '',
                isSetDisabled ? cn.actionBtnDisabled : '',
              ]
                .filter(Boolean)
                .join(' ')}
              onClick={() => {
                if (!isSetDisabled) {
                  setAction('SET')
                  if (
                    activeTab === 'MAIN' &&
                    selectedMainField &&
                    selectedDetails.length === 1
                  ) {
                    const field = MAIN_FIELDS.find(
                      (f) => f.key === selectedMainField
                    )
                    if (field && !field.readonly) {
                      const model = selectedDetails[0].nomenclatureExtendedModel
                      setProcessValue(
                        field.type.startsWith('REFERENCE_')
                          ? getMainFieldIdValue(model, selectedMainField)
                          : getMainFieldDisplayValue(model, selectedMainField)
                      )
                      setProcessValues(
                        getMainFieldListValues(model, selectedMainField)
                      )
                      return
                    }
                  } else if (
                    activeTab === 'ADDITIONAL' &&
                    selectedChrcCode !== null &&
                    selectedDetails.length === 1
                  ) {
                    const chrc = selectedDetails[0].characteristics.find(
                      (c) => c.chrcCode === selectedChrcCode
                    )
                    // У слотовой характеристики значение живёт в processSlotValues
                    // (valueId по присоединениям), а chrcValue — это готовая строка
                    // «32 × 25» для показа. В payload она не участвует, класть её
                    // в processValue незачем.
                    if (!isSlottedCharacteristicType(chrc?.valueType)) {
                      // REFERENCE: на бэк уходит UUID — берём chrcValueId
                      // (в chrcValue лежит текст). Для остальных типов — chrcValue.
                      setProcessValue(
                        chrc?.valueType === 'REFERENCE'
                          ? (chrc?.chrcValueId ??
                              dictionaryValues.find(
                                (d) => d.value === chrc?.chrcValue
                              )?.id ??
                              '')
                          : (chrc?.chrcValue ?? '')
                      )
                    }
                    return
                  }
                  setProcessValue('')
                }
              }}
              disabled={isRightPanelDisabled || isSetDisabled}
              title={
                isSetDisabled
                  ? 'Редактирование недоступно для этого реквизита'
                  : undefined
              }
            >
              Установить
            </button>
            {isListFieldSelected && (
              <button
                className={[
                  cn.actionBtn,
                  action === 'ADD' ? cn.actionBtnActive : '',
                ]
                  .filter(Boolean)
                  .join(' ')}
                onClick={() => {
                  setAction('ADD')
                  setProcessValue('')
                  setProcessValues([])
                }}
                disabled={isRightPanelDisabled}
              >
                Добавить
              </button>
            )}
            <button
              className={[
                cn.actionBtn,
                action === 'CLEAR' ? cn.actionBtnActive : '',
                isClearDisabled ? cn.actionBtnDisabled : '',
              ]
                .filter(Boolean)
                .join(' ')}
              onClick={() => {
                if (!isClearDisabled) {
                  setAction('CLEAR')
                  setProcessValue('')
                  setProcessValues([])
                }
              }}
              disabled={isRightPanelDisabled || isClearDisabled}
              title={
                isClearDisabled
                  ? currentMainField?.readonly
                    ? 'Редактирование недоступно для этого реквизита'
                    : 'Очистка недоступна для этого реквизита'
                  : undefined
              }
            >
              Очистить
            </button>
          </div>
        </div>

        {action === 'CLEAR' && isListFieldSelected && (
          <div className={[cn.processingRow, cn.processingRowTop].join(' ')}>
            <label className={cn.processingLabel}>Удалить</label>
            <div className={cn.valueInputWrap}>
              <SupplierArticlesClearPicker
                options={supplierArticleOptions}
                value={processValues}
                onChange={setProcessValues}
              />
            </div>
          </div>
        )}

        {action !== 'CLEAR' && !currentMainField?.readonly && (
          <div className={[cn.processingRow, cn.processingRowTop].join(' ')}>
            {selectedMainField === 'DETAILS' ? (
              <div className={cn.detailsLabelCol}>
                <label className={cn.processingLabel}>Значение</label>
                <button
                  className={cn.processBtnLabel}
                  onClick={handleProcess}
                  disabled={isProcessDisabled || processing}
                >
                  {processing ? 'Обработка...' : 'Обработать'}
                </button>
              </div>
            ) : (
              <label className={cn.processingLabel}>Значение</label>
            )}
            <div className={cn.valueInputWrap}>
              <ValueInput />
            </div>
          </div>
        )}

        {!(selectedMainField === 'DETAILS' && action === 'SET') && (
          <button
            className={cn.processBtn}
            onClick={handleProcess}
            disabled={isProcessDisabled || processing}
          >
            {processing ? 'Обработка...' : 'Обработать'}
          </button>
        )}
      </div>

      {selectedIds.size > 1 && (
        <div className={cn.multiSelectBanner}>
          {activeTab === 'ADDITIONAL'
            ? 'Выбрано несколько номенклатур. Показаны только общие характеристики'
            : 'Выбрано несколько номенклатур. Показаны только совпадающие значения'}
        </div>
      )}

      <div
        className={`${cn.infoSection} ${selectedIds.size > 1 ? cn.infoSectionCompact : ''}`}
      >
        {isPanelLoading ? (
          <div className={cn.tableCenter}>
            <Loader size={24} />
          </div>
        ) : isRightPanelDisabled ? (
          <div className={cn.rightPanelPlaceholder}>
            <span>Выберите номенклатуры в левой таблице</span>
          </div>
        ) : (
          <InfoTable />
        )}
      </div>

      {!productImagesLoading &&
        productImages.length > 0 &&
        singleCode !== null && (
          <div className={cn.productImages}>
            {productImages.map((_, idx) => (
              <div key={idx} className={cn.productImage}>
                <Image
                  productCode={singleCode}
                  imageIndex={idx}
                  className={cn.productImageWrapper}
                  hideOnError
                  disableLoader={idx > 0}
                />
              </div>
            ))}
          </div>
        )}
    </div>
  )
}

export default ProcessingTab
